import { z } from "zod";
import { scoped, type Args, type Operation } from "../types";

export const observationSourceSchema = z.enum(["claude", "codex", "gemini", "grok"]);
export const observationTierSchema = z.literal("aggregate");
export const observationDurationSchema = z.enum(["once", "source"]);

export const observationSourceStatusSchema = z.object({
  source: observationSourceSchema,
  label: z.string(),
  installed: z.boolean().nullable(),
  linked: z.boolean().nullable(),
  registrationMetadataInspected: z.boolean(),
  presence: z.enum(["detected", "not_detected", "not_checked"]),
  connectionFailure: z
    .object({
      code: z.string(),
      message: z.string(),
      retryable: z.boolean(),
    })
    .nullable(),
  discovery: z.enum(["aggregate", "metadata-only"]),
  detail: z.string(),
});

export const observationGrantSchema = z.object({
  id: z.string(),
  source: observationSourceSchema,
  tier: observationTierSchema,
  duration: observationDurationSchema,
  retentionDays: z.literal(0),
  pathLabel: z.string(),
  processing: z.literal("local-only"),
  egress: z.literal("none"),
  grantedAt: z.string(),
  expiresAt: z.string(),
  active: z.boolean(),
});

const reviewedCandidateSchema = z.object({
  id: z.string(),
  discoveryId: z.string(),
  grantId: z.string(),
  candidateId: z.string(),
  label: z.string(),
  reviewedAt: z.string(),
});

const statusResponse = z.object({
  sources: z.array(observationSourceStatusSchema),
  activeGrants: z.array(observationGrantSchema),
  reviewedCandidates: z.array(reviewedCandidateSchema),
});

const grantBody = z.object({
  source: observationSourceSchema,
  tier: observationTierSchema,
  duration: observationDurationSchema,
  retentionDays: z.literal(0),
});

const grantResponse = z.object({ grant: observationGrantSchema });
const grantIdBody = z.object({ grantId: z.string().min(1).max(200) });
const revokeResponse = z.object({ grantId: z.string(), revoked: z.literal(true) });

const categoryCountsSchema = z.object({
  fileIo: z.number().int().nonnegative(),
  shell: z.number().int().nonnegative(),
  agent: z.number().int().nonnegative(),
  web: z.number().int().nonnegative(),
  planning: z.number().int().nonnegative(),
  mcp: z.number().int().nonnegative(),
  skill: z.number().int().nonnegative(),
  other: z.number().int().nonnegative(),
});

const discoveryResponse = z.object({
  discoveryId: z.string(),
  /** Ephemeral proof held by the originating browser so a post-login review
   * can claim this anonymous local discovery. Only its hash is persisted. */
  reviewClaimToken: z.string().length(64),
  grantId: z.string(),
  source: observationSourceSchema,
  observedAt: z.string(),
  sessionFilesRead: z.boolean(),
  promptTextReturned: z.literal(false),
  egress: z.literal("none"),
  aggregate: z.object({
    available: z.boolean(),
    sessionCount: z.number().int().nonnegative(),
    totalToolCalls: z.number().int().nonnegative(),
    categoryCounts: categoryCountsSchema,
    topTools: z.array(
      z.object({
        name: z.string(),
        count: z.number().int().nonnegative(),
        category: z.string(),
      }),
    ),
  }),
  candidates: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      basis: z.string(),
      coverage: z.enum(["aggregate", "partial"]),
    }),
  ),
});

const reviewBody = z.object({
  discoveryId: z.string().min(1).max(200),
  candidateId: z.string().min(1).max(100),
  reviewClaimToken: z.string().length(64),
});
const reviewResponse = z.object({
  review: statusResponse.shape.reviewedCandidates.element,
});

const reviewIdParams = z.object({ id: z.string().min(1).max(200) });

const firstUnknownReviewQuery = z
  .object({
    projectName: z.string().min(1).max(200).optional(),
    projectRoot: z.string().min(1).max(4096).optional(),
    gateId: z.string().min(1).max(200).optional(),
    command: z.string().min(1).max(4096).optional(),
  })
  .optional();

const firstUnknownCardSchema = z.object({
  id: z.string(),
  label: z.string(),
  domainExtension: z.string(),
  extensionVersion: z.string(),
  classification: z.enum(["task_outcome", "baseline", "safety", "effect_closure"]),
  whatHappened: z.string(),
  willCheck: z.string(),
  why: z.string(),
  evidenceOracles: z.array(z.string()),
  blindSpots: z.array(z.string()),
  requiredPermission: z.string(),
  required: z.boolean(),
  protected: z.boolean(),
  assuranceImpactIfDropped: z.enum(["none", "lower", "materially_lower", "not_verifiable"]),
  sources: z.array(z.string()),
  falsifiers: z.array(z.string()),
  admissionStatus: z.enum(["admitted", "gap", "human_required"]),
  gaps: z.array(z.string()),
  proposedObligation: z.unknown(),
});

const firstUnknownReviewSchema = z.object({
  reviewId: z.string(),
  candidateDigest: z.string(),
  candidateLabel: z.string(),
  domainExtension: z.string(),
  extensionVersion: z.string(),
  customerFacingState: z.literal("not_yet_eligible"),
  historicalOccurrenceVerification: z.literal("not_run"),
  selectedCardIds: z.array(z.string()),
  cards: z.array(firstUnknownCardSchema),
  acceptedContract: z
    .object({
      contractId: z.string(),
      contractDigest: z.string(),
      acceptedAt: z.string(),
      status: z.enum(["ACCEPTED", "UNKNOWN"]),
    })
    .nullable(),
  rejection: z
    .object({
      candidateDigest: z.string(),
      reason: z.string(),
      rejectedAt: z.string(),
    })
    .nullable()
    .optional(),
});

const firstUnknownReviewResponse = z.object({
  review: firstUnknownReviewSchema,
});

const confirmFirstUnknownReviewBody = z.object({
  expectedCandidateDigest: z.string().min(1).max(200),
  selectedCardIds: z.array(z.string().min(1).max(200)).min(1),
  idempotencyKey: z.string().min(1).max(200),
  policyAuthority: z.boolean().optional(),
  oracleContext: z
    .object({
      projectName: z.string().min(1).max(200),
      projectRoot: z.string().min(1).max(4096),
      gateId: z.string().min(1).max(200),
      command: z.string().min(1).max(4096),
    })
    .nullable()
    .optional(),
});

const confirmFirstUnknownRejectedCodeSchema = z.enum([
  "candidate-stale",
  "policy-authority-required",
  "idempotency-conflict",
  "admission-gap",
  "candidate-not-found",
  "save-uncertain",
]);

const confirmFirstUnknownAcceptedResponse = z.object({
  status: z.literal("accepted"),
  acceptance: z.object({
    reviewId: z.string(),
    acceptedAt: z.string(),
    reviewCandidateDigest: z.string(),
    selectedCardIds: z.array(z.string()),
    acceptedContractDigest: z.string(),
    idempotencyKey: z.string(),
    recurrenceBinding: z.object({
      workItemId: z.string(),
      appliesTo: z.literal("next_occurrence"),
      excludedOccurrenceId: z.string(),
      historicalOccurrenceVerification: z.literal("not_run"),
    }),
  }),
  acceptedContract: z.object({
    contractId: z.string(),
    contractDigest: z.string(),
    acceptedAt: z.string(),
    status: z.literal("ACCEPTED"),
  }),
});

const confirmFirstUnknownRejectedResponse = z.object({
  status: z.literal("rejected"),
  error: z.object({
    code: confirmFirstUnknownRejectedCodeSchema,
    message: z.string(),
    details: z.array(z.string()),
  }),
  acceptance: z.null(),
  acceptedContract: z.null(),
});

const confirmFirstUnknownReviewResponse = z.discriminatedUnion("status", [
  confirmFirstUnknownAcceptedResponse,
  confirmFirstUnknownRejectedResponse,
]);

const status: Operation<void, void, void, z.infer<typeof statusResponse>> = {
  method: "GET",
  path: () => "/api/verification/observation",
  response: statusResponse,
};

const grant: Operation<void, void, z.infer<typeof grantBody>, z.infer<typeof grantResponse>> = {
  method: "POST",
  path: () => "/api/verification/observation/grant",
  body: grantBody,
  response: grantResponse,
  invalidates: [status],
};

const discover: Operation<
  void,
  void,
  z.infer<typeof grantIdBody>,
  z.infer<typeof discoveryResponse>
> = {
  method: "POST",
  path: () => "/api/verification/observation/discover",
  body: grantIdBody,
  response: discoveryResponse,
  invalidates: [status],
};

const revoke: Operation<void, void, z.infer<typeof grantIdBody>, z.infer<typeof revokeResponse>> = {
  method: "POST",
  path: () => "/api/verification/observation/revoke",
  body: grantIdBody,
  response: revokeResponse,
  invalidates: [status],
};

const review: Operation<void, void, z.infer<typeof reviewBody>, z.infer<typeof reviewResponse>> = {
  method: "POST",
  path: () => "/api/verification/observation/review",
  body: reviewBody,
  response: reviewResponse,
  invalidates: [status],
};

const firstUnknownReview: Operation<
  z.infer<typeof reviewIdParams>,
  z.infer<typeof firstUnknownReviewQuery>,
  void,
  z.infer<typeof firstUnknownReviewResponse>
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/observation/reviews/${encodeURIComponent(id)}/first-unknown`,
  params: reviewIdParams,
  query: firstUnknownReviewQuery,
  response: firstUnknownReviewResponse,
};

const rejectFirstUnknownReviewBody = z.object({
  expectedCandidateDigest: z.string().min(1).max(200),
  idempotencyKey: z.string().min(1).max(200),
  reason: z.string().min(1).max(4096),
  oracleContext: z
    .object({
      projectName: z.string().min(1).max(200),
      projectRoot: z.string().min(1).max(4096),
      gateId: z.string().min(1).max(200),
      command: z.string().min(1).max(4096),
    })
    .nullable()
    .optional(),
});

const rejectFirstUnknownReviewResponse = z.object({
  status: z.literal("rejected"),
  rejection: z.object({
    candidateDigest: z.string(),
    reason: z.string(),
    rejectedAt: z.string(),
  }),
});

const rejectFirstUnknownReview: Operation<
  z.infer<typeof reviewIdParams>,
  void,
  z.infer<typeof rejectFirstUnknownReviewBody>,
  z.infer<typeof rejectFirstUnknownReviewResponse>
> = {
  method: "POST",
  path: ({ id }) =>
    `/api/verification/observation/reviews/${encodeURIComponent(id)}/first-unknown/reject`,
  params: reviewIdParams,
  body: rejectFirstUnknownReviewBody,
  response: rejectFirstUnknownReviewResponse,
  invalidates: [
    status,
    scoped(
      firstUnknownReview,
      (
        args: Args<
          z.infer<typeof reviewIdParams>,
          void,
          z.infer<typeof rejectFirstUnknownReviewBody>
        >,
      ) => ({
        id: args.params.id,
      }),
    ),
  ],
};

const confirmFirstUnknownReview: Operation<
  z.infer<typeof reviewIdParams>,
  void,
  z.infer<typeof confirmFirstUnknownReviewBody>,
  z.infer<typeof confirmFirstUnknownReviewResponse>
> = {
  method: "POST",
  path: ({ id }) =>
    `/api/verification/observation/reviews/${encodeURIComponent(id)}/first-unknown/confirm`,
  params: reviewIdParams,
  body: confirmFirstUnknownReviewBody,
  response: confirmFirstUnknownReviewResponse,
  invalidates: [
    status,
    scoped(
      firstUnknownReview,
      (
        args: Args<
          z.infer<typeof reviewIdParams>,
          void,
          z.infer<typeof confirmFirstUnknownReviewBody>
        >,
      ) => ({
        id: args.params.id,
      }),
    ),
  ],
};

export type ObservationSource = z.infer<typeof observationSourceSchema>;
export type ObservationGrant = z.infer<typeof observationGrantSchema>;
export type ObservationGrantBody = z.infer<typeof grantBody>;
export type ObservationDiscovery = z.infer<typeof discoveryResponse>;
export type ObservationReview = z.infer<typeof reviewResponse>["review"];
export type ObservationFirstUnknownReview = z.infer<typeof firstUnknownReviewResponse>["review"];
export type ObservationFirstUnknownAcceptance = z.infer<typeof confirmFirstUnknownReviewResponse>;

export const verificationObservation = {
  status,
  grant,
  discover,
  review,
  firstUnknownReview,
  confirmFirstUnknownReview,
  rejectFirstUnknownReview,
  revoke,
};
